import { useParams, Navigate, Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import SEO from "../components/common/SEO";
import PageHero from "../components/common/PageHero";
import { Reveal } from "../components/common/Reveal";
import PillButton from "../components/common/PillButton";
import CTASection from "../components/common/CTASection";
import { Icon } from "../lib/icon";
import { useContent } from "../context/ContentContext";
import { useStaffing } from "../components/forms/StaffingProvider";

export default function IndustryDetail() {
  const { slug } = useParams();
  const { getIndustry, industries } = useContent();
  const industry = getIndustry(slug);
  const { open } = useStaffing();
  if (!industry) return <Navigate to="/industries" replace />;

  return (
    <>
      <SEO title={industry.name} description={industry.tagline} path={`/industries/${slug}`} />
      <PageHero eyebrow="Industry" title={industry.name} subtitle={industry.tagline} image={industry.image} neural>
        <div className="flex flex-col gap-4 sm:flex-row">
          <PillButton onClick={open} size="lg" testId="industry-hero-cta">{industry.cta}</PillButton>
          {slug === "semiconductor-electronics" && (
            <PillButton to="/semiconductor" variant="outline" size="lg" testId="industry-spotlight-link">
              View Semiconductor Spotlight
            </PillButton>
          )}
        </div>
      </PageHero>

      <section className="px-6 py-12 lg:py-20">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-12">
          {/* Sticky sidebar */}
          <aside className="hidden lg:col-span-3 lg:block">
            <div className="sticky top-28">
              <div className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                All Industries
              </div>
              <nav className="space-y-1 border-l border-white/10">
                {industries.map((ind) => (
                  <Link
                    key={ind.slug}
                    to={`/industries/${ind.slug}`}
                    className={`-ml-px flex items-center gap-2 border-l-2 py-2 pl-4 text-sm transition-colors ${
                      ind.slug === slug
                        ? "border-sky-400 text-sky-400"
                        : "border-transparent text-slate-400 hover:border-white/30 hover:text-white"
                    }`}
                    data-testid={`industry-nav-${ind.slug}`}
                  >
                    {ind.name}
                  </Link>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <div className="lg:col-span-9">
            <Reveal>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-white lg:text-3xl">
                Industry Overview
              </h2>
              <div className="mt-5 space-y-4 text-base leading-relaxed text-slate-400">
                {industry.overview.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </Reveal>

            <div className="mt-14 grid gap-6 md:grid-cols-2">
              <Reveal>
                <div className="h-full rounded-2xl border border-white/10 bg-ink-800/50 p-8">
                  <h3 className="font-heading text-xl font-bold tracking-tight text-white">
                    Technology Solutions
                  </h3>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {industry.technology.map((t) => (
                      <span key={t} className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-slate-300">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="h-full rounded-2xl border border-white/10 bg-ink-800/50 p-8">
                  <h3 className="font-heading text-xl font-bold tracking-tight text-white">
                    Talent Solutions
                  </h3>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {industry.talent.map((t) => (
                      <span key={t} className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-slate-300">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>

            <Reveal className="mt-14">
              <h3 className="font-heading text-xl font-bold tracking-tight text-white">Common Roles</h3>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {industry.roles.map((r) => (
                  <li key={r} className="flex items-center gap-3 rounded-xl border border-white/10 bg-ink-800/40 px-4 py-3 text-sm text-slate-300">
                    <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-400/15 text-sky-400">
                      <Check className="h-3 w-3" />
                    </span>
                    {r}
                  </li>
                ))}
              </ul>
            </Reveal>

            <div className="mt-12 flex flex-col items-start gap-4 rounded-2xl border border-white/10 bg-gradient-to-br from-ink-800/80 to-ink-800/30 p-8 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-sky-400">
                  <Icon name={industry.icon} className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-heading text-xl font-bold tracking-tight text-white">
                  {industry.cta}
                </h3>
              </div>
              <PillButton onClick={open} testId="industry-content-cta">{industry.cta}</PillButton>
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
